import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import {
  catchError,
  EMPTY,
  map,
  mergeMap,
  MonoTypeOperatorFunction,
  of,
  switchMap,
  takeUntil,
  tap,
} from 'rxjs';

import { EffectsBase, fromSocketEvent } from '@ps/core-data-access';
import { ToastService } from '@ps/core-overlay';

import {
  SurveyService,
  PsidService,
  SurveyActivityLogService,
  SurveyCommentsService,
  SurveyListActions,
  SURVEY_SOCKET_EVENTS,
} from '@ps/survey-data-access';

import {
  ManageSurveyActions,
  ManageSurveyApiActions,
} from './manage-survey.actions';
import {
  selectQuotaLoadedPage,
  selectQuotaSearch,
} from './manage-survey.selectors';

@Injectable()
export class ManageSurveyEffects extends EffectsBase {
  private readonly _actions$ = inject(Actions);
  private readonly _store = inject(Store);
  private readonly _surveyService = inject(SurveyService);
  private readonly _psidService = inject(PsidService);
  private readonly _activityLogService = inject(SurveyActivityLogService);
  private readonly _commentsService = inject(SurveyCommentsService);
  private readonly _toast = inject(ToastService);

  // Handles both the lightweight preview-drawer load (`loadSurveyDetails`,
  // plain `getById`) and the full Manage Survey page load
  // (`loadFullSurveyDetails`, `getByIdTargeting`) — same success/failure
  // actions either way, so one effect picks the right service call per
  // action instead of duplicating the pipeline.
  public loadSurvey$ = createEffect(() =>
    this._actions$.pipe(
      ofType(
        ManageSurveyActions.loadSurveyDetails,
        ManageSurveyActions.loadFullSurveyDetails,
      ),
      switchMap((action) => {
        const survey$ =
          action.type === ManageSurveyActions.loadFullSurveyDetails.type
            ? this._surveyService.getByIdTargeting(action.surveyId)
            : this._surveyService.getById(action.surveyId);

        return survey$.pipe(
          map((survey) => ManageSurveyApiActions.loadSurveySuccess({ survey })),
          this.psCatchError(
            (err) =>
              of(
                ManageSurveyApiActions.loadSurveyFailure({
                  error: err.message ?? 'Failed to load survey',
                }),
              ),
            { notify: true },
          ),
        );
      }),
    ),
  );

  public loadChildSurvey$ = createEffect(() =>
    this._actions$.pipe(
      ofType(ManageSurveyActions.loadChildSurveyDetails),
      switchMap(({ surveyId }) =>
        this._surveyService.getById(surveyId, 0, true, 'page').pipe(
          map((survey) =>
            ManageSurveyApiActions.loadChildSurveySuccess({ survey }),
          ),
          this.psCatchError(
            (err) =>
              of(
                ManageSurveyApiActions.loadChildSurveyFailure({
                  error: err.message ?? 'Failed to load survey',
                }),
              ),
            { notify: true },
          ),
        ),
      ),
    ),
  );

  public loadSurveyHealth$ = createEffect(() =>
    this._actions$.pipe(
      ofType(ManageSurveyActions.loadSurveyHealth),
      switchMap(({ surveyId }) =>
        this._surveyService.getHealth(surveyId).pipe(
          map((healthMetrics) =>
            ManageSurveyApiActions.loadSurveyHealthSuccess({ healthMetrics }),
          ),
          this.psCatchError(
            (err) =>
              of(
                ManageSurveyApiActions.loadSurveyHealthFailure({
                  error: err.message ?? 'Failed to load survey health',
                }),
              ),
            { notify: true },
          ),
        ),
      ),
    ),
  );

  public loadRemainingHours$ = createEffect(() =>
    this._actions$.pipe(
      ofType(ManageSurveyActions.loadRemainingHours),
      switchMap(({ surveyId }) =>
        this._surveyService.getRemainingHours(surveyId).pipe(
          map((remainingHours) =>
            ManageSurveyApiActions.loadRemainingHoursSuccess({
              remainingHours,
            }),
          ),
          this.psCatchError(
            (err) =>
              of(
                ManageSurveyApiActions.loadRemainingHoursFailure({
                  error: err.message ?? 'Failed to load remaining hours',
                }),
              ),
            { notify: true },
          ),
        ),
      ),
    ),
  );

  public loadActivityLogs$ = createEffect(() =>
    this._actions$.pipe(
      ofType(ManageSurveyActions.loadActivityLogs),
      switchMap(({ surveyId }) =>
        this._activityLogService.getBySurveyId(surveyId).pipe(
          map((activityLogs) =>
            ManageSurveyApiActions.loadActivityLogsSuccess({ activityLogs }),
          ),
          this.psCatchError(
            (err) =>
              of(
                ManageSurveyApiActions.loadActivityLogsFailure({
                  error: err.message ?? 'Failed to load activity logs',
                }),
              ),
            { notify: true },
          ),
        ),
      ),
    ),
  );

  public loadComments$ = createEffect(() =>
    this._actions$.pipe(
      ofType(ManageSurveyActions.loadComments),
      switchMap(({ surveyId }) =>
        this._commentsService.commentsForSurvey(surveyId).pipe(
          map((comments) =>
            ManageSurveyApiActions.loadCommentsSuccess({ comments }),
          ),
          this.psCatchError((error) =>
            of(
              ManageSurveyApiActions.loadCommentsFailure({
                error: error.message,
              }),
            ),
          ),
        ),
      ),
    ),
  );

  public addComment$ = createEffect(() =>
    this._actions$.pipe(
      ofType(ManageSurveyActions.addComment),
      switchMap(({ surveyId, body }) =>
        this._commentsService.createComment(surveyId, body).pipe(
          map((comment) =>
            ManageSurveyApiActions.addCommentSuccess({ comment }),
          ),
          this.psCatchError((error) =>
            of(
              ManageSurveyApiActions.addCommentFailure({
                error: error.message,
              }),
            ),
          ),
        ),
      ),
    ),
  );

  public loadSurveyQuotas$ = createEffect(() =>
    this._actions$.pipe(
      ofType(ManageSurveyActions.loadSurveyQuotas),
      switchMap(({ surveyId, page, search }) =>
        this._surveyService.getQuotas(surveyId, page, search).pipe(
          map(({ quotas, total }) =>
            ManageSurveyApiActions.loadSurveyQuotasSuccess({ quotas, total }),
          ),
          this.psCatchError(
            (err) =>
              of(
                ManageSurveyApiActions.loadSurveyQuotasFailure({
                  error: err.message ?? 'Failed to load quotas',
                }),
              ),
            { notify: true },
          ),
          this._untilQuotasReset(),
        ),
      ),
    ),
  );

  public readonly updateCurrentCpi$ = createEffect(() =>
    this._actions$.pipe(
      ofType(ManageSurveyActions.updateCurrentCpi),
      switchMap(({ surveyId, cpi, isChild }) =>
        this._surveyService
          .updateCurrentCpi(surveyId, cpi, isChild, 'drawer')
          .pipe(
            map((survey) =>
              isChild
                ? ManageSurveyApiActions.updateChildCurrentCpiSuccess({
                    survey,
                  })
                : ManageSurveyApiActions.updateCurrentCpiSuccess({ survey }),
            ),
            this.psCatchError(
              (err) =>
                of(
                  ManageSurveyApiActions.updateCurrentCpiFailure({
                    error: err.message ?? 'Failed to update CPI',
                  }),
                ),
              { notify: true },
            ),
          ),
      ),
    ),
  );

  public loadMoreQuotas$ = createEffect(() =>
    this._actions$.pipe(
      ofType(ManageSurveyActions.loadMoreQuotas),
      concatLatestFrom(() => [
        this._store.select(selectQuotaLoadedPage),
        this._store.select(selectQuotaSearch),
      ]),
      switchMap(([{ surveyId }, page, search]) =>
        this._surveyService.getQuotas(surveyId, page, search).pipe(
          map(({ quotas }) =>
            ManageSurveyApiActions.loadMoreQuotasSuccess({ quotas }),
          ),
          this.psCatchError(
            () => of(ManageSurveyApiActions.loadMoreQuotasFailure()),
            { notify: true },
          ),
          this._untilQuotasReset(),
        ),
      ),
    ),
  );

  public loadAllQuotas$ = createEffect(() =>
    this._actions$.pipe(
      ofType(ManageSurveyActions.loadAllQuotas),
      concatLatestFrom(() => this._store.select(selectQuotaSearch)),
      switchMap(([{ surveyId }, search]) =>
        this._surveyService.getQuotas(surveyId, undefined, search).pipe(
          map(({ quotas }) =>
            ManageSurveyApiActions.loadAllQuotasSuccess({ quotas }),
          ),
          this.psCatchError(
            () => of(ManageSurveyApiActions.loadAllQuotasFailure()),
            { notify: true },
          ),
          this._untilQuotasReset(),
        ),
      ),
    ),
  );

  public setQuotaLock$ = createEffect(() =>
    this._actions$.pipe(
      ofType(ManageSurveyActions.setQuotaLock),
      mergeMap(({ surveyId, psQuotaId, locked }) =>
        this._surveyService.setQuotaLock(surveyId, psQuotaId, locked).pipe(
          map(() =>
            ManageSurveyApiActions.setQuotaLockSuccess({ psQuotaId, locked }),
          ),
          this.psCatchError(
            (err) =>
              of(
                ManageSurveyApiActions.setQuotaLockFailure({
                  psQuotaId,
                  error: err.message ?? 'Failed to update quota lock',
                }),
              ),
            { notify: true },
          ),
        ),
      ),
    ),
  );

  public setQuotaCurrentTarget$ = createEffect(() =>
    this._actions$.pipe(
      ofType(ManageSurveyActions.setQuotaCurrentTarget),
      mergeMap(({ surveyId, psQuotaId, currentTarget, updateSupplier }) =>
        this._surveyService
          .setQuotaCurrentTarget(
            surveyId,
            psQuotaId,
            currentTarget,
            updateSupplier,
          )
          .pipe(
            map((update) =>
              ManageSurveyApiActions.setQuotaCurrentTargetSuccess({
                psQuotaId,
                update,
              }),
            ),
            this.psCatchError(
              (err) =>
                of(
                  ManageSurveyApiActions.setQuotaCurrentTargetFailure({
                    psQuotaId,
                    error: err.message ?? 'Failed to update quota target',
                  }),
                ),
              { notify: true },
            ),
          ),
      ),
    ),
  );

  public setQuotaCurrentlyOpen$ = createEffect(() =>
    this._actions$.pipe(
      ofType(ManageSurveyActions.setQuotaCurrentlyOpen),
      mergeMap(({ surveyId, psQuotaId, currentlyOpen }) =>
        this._surveyService
          .setQuotaCurrentlyOpen(surveyId, psQuotaId, currentlyOpen)
          .pipe(
            map((update) =>
              ManageSurveyApiActions.setQuotaCurrentlyOpenSuccess({
                psQuotaId,
                update,
              }),
            ),
            this.psCatchError(
              (err) =>
                of(
                  ManageSurveyApiActions.setQuotaCurrentlyOpenFailure({
                    psQuotaId,
                    error: err.message ?? 'Failed to update currently open',
                  }),
                ),
              { notify: true },
            ),
          ),
      ),
    ),
  );

  public setSupplierQuotaCurrentTarget$ = createEffect(() =>
    this._actions$.pipe(
      ofType(ManageSurveyActions.setSupplierQuotaCurrentTarget),
      mergeMap(({ surveyId, psQuotaId, supplierRefId, currentTarget }) =>
        this._surveyService
          .setSupplierQuotaField(
            surveyId,
            psQuotaId,
            supplierRefId,
            'currentTarget',
            currentTarget,
          )
          .pipe(
            map(() =>
              ManageSurveyApiActions.setSupplierQuotaCurrentTargetSuccess({
                psQuotaId,
                supplierRefId,
                currentTarget,
              }),
            ),
            this.psCatchError(
              (err) =>
                of(
                  ManageSurveyApiActions.setSupplierQuotaCurrentTargetFailure({
                    psQuotaId,
                    supplierRefId,
                    error: err.message ?? 'Failed to update supplier target',
                  }),
                ),
              { notify: true },
            ),
          ),
      ),
    ),
  );

  public setSupplierQuotaCurrentlyOpen$ = createEffect(() =>
    this._actions$.pipe(
      ofType(ManageSurveyActions.setSupplierQuotaCurrentlyOpen),
      mergeMap(({ surveyId, psQuotaId, supplierRefId, currentlyOpen }) =>
        this._surveyService
          .setSupplierQuotaField(
            surveyId,
            psQuotaId,
            supplierRefId,
            'currentlyOpen',
            currentlyOpen,
          )
          .pipe(
            map(() =>
              ManageSurveyApiActions.setSupplierQuotaCurrentlyOpenSuccess({
                psQuotaId,
                supplierRefId,
                currentlyOpen,
              }),
            ),
            this.psCatchError(
              (err) =>
                of(
                  ManageSurveyApiActions.setSupplierQuotaCurrentlyOpenFailure({
                    psQuotaId,
                    supplierRefId,
                    error:
                      err.message ?? 'Failed to update supplier currently open',
                  }),
                ),
              { notify: true },
            ),
          ),
      ),
    ),
  );

  public renameQuota$ = createEffect(() =>
    this._actions$.pipe(
      ofType(ManageSurveyActions.renameQuota),
      mergeMap(({ surveyId, psQuotaId, label }) =>
        this._surveyService.renameQuota(surveyId, psQuotaId, label).pipe(
          map(() =>
            ManageSurveyApiActions.renameQuotaSuccess({ psQuotaId, label }),
          ),
          this.psCatchError(
            (err) =>
              of(
                ManageSurveyApiActions.renameQuotaFailure({
                  psQuotaId,
                  error: err.message ?? 'Failed to rename quota',
                }),
              ),
            { notify: true },
          ),
        ),
      ),
    ),
  );

  public setQuotaCpi$ = createEffect(() =>
    this._actions$.pipe(
      ofType(ManageSurveyActions.setQuotaCpi),
      mergeMap(({ surveyId, psQuotaId, cpi }) =>
        this._surveyService.setQuotaCpi(surveyId, psQuotaId, cpi).pipe(
          map(() =>
            ManageSurveyApiActions.setQuotaCpiSuccess({
              surveyId,
              psQuotaId,
              cpi,
            }),
          ),
          this.psCatchError(
            (err) =>
              of(
                ManageSurveyApiActions.setQuotaCpiFailure({
                  psQuotaId,
                  error: err.message ?? 'Failed to update quota CPI',
                }),
              ),
            { notify: true },
          ),
        ),
      ),
    ),
  );

  // Fire-and-forget analytics ping — mirrors legacy's non-blocking
  // pushApplyPriceRecoButtonCount call, kept as its own effect so a change to
  // one concern never risks the other; errors here must never surface to the
  // store, since the CPI update itself already succeeded.
  public pushApplyPriceRecoButtonCount$ = createEffect(
    () =>
      this._actions$.pipe(
        ofType(ManageSurveyApiActions.setQuotaCpiSuccess),
        mergeMap(({ surveyId }) =>
          this._surveyService
            .pushApplyPriceRecoButtonCount(surveyId)
            .pipe(catchError(() => EMPTY)),
        ),
      ),
    { dispatch: false },
  );

  // Traffic channels are a manage-survey-only concept, so this listens for
  // the socket event directly rather than relaying it in from data-access.
  public trafficChannelUpdates$ = createEffect(() =>
    fromSocketEvent<number>(SURVEY_SOCKET_EVENTS.TRAFFIC_CHANNEL_UPDATES).pipe(
      map((surveyId) => ManageSurveyActions.loadTrafficChannels({ surveyId })),
    ),
  );

  public loadTrafficChannels$ = createEffect(() =>
    this._actions$.pipe(
      ofType(ManageSurveyActions.loadTrafficChannels),
      switchMap(({ surveyId }) =>
        this._surveyService.getTrafficChannelChildren(surveyId).pipe(
          map((trafficChannels) =>
            ManageSurveyApiActions.loadTrafficChannelsSuccess({
              trafficChannels,
            }),
          ),
          this.psCatchError(
            (err) =>
              of(
                ManageSurveyApiActions.loadTrafficChannelsFailure({
                  error: err.message ?? 'Failed to load traffic channels',
                }),
              ),
            { notify: true },
          ),
        ),
      ),
    ),
  );

  public setTrafficChannelStatus$ = createEffect(() =>
    this._actions$.pipe(
      ofType(ManageSurveyActions.setTrafficChannelStatus),
      mergeMap(({ surveyId, status }) =>
        this._surveyService.updateSurveyStatus(surveyId, status).pipe(
          map(() =>
            ManageSurveyApiActions.setTrafficChannelStatusSuccess({
              surveyId,
              status,
            }),
          ),
          this.psCatchError(
            (err) =>
              of(
                ManageSurveyApiActions.setTrafficChannelStatusFailure({
                  surveyId,
                  error:
                    err.message ?? 'Failed to update traffic channel status',
                }),
              ),
            { notify: true },
          ),
        ),
      ),
    ),
  );

  public launchTrafficChannel$ = createEffect(() =>
    this._actions$.pipe(
      ofType(ManageSurveyActions.launchTrafficChannel),
      mergeMap(({ surveyId, event }) =>
        this._surveyService.launchTrafficChannel(surveyId, event).pipe(
          map((trafficChannels) =>
            ManageSurveyApiActions.launchTrafficChannelSuccess({
              surveyId,
              trafficChannels,
            }),
          ),
          this.psCatchError(
            (err) =>
              of(
                ManageSurveyApiActions.launchTrafficChannelFailure({
                  surveyId,
                  error: err.message ?? 'Failed to launch traffic channel',
                }),
              ),
            { notify: true },
          ),
        ),
      ),
    ),
  );

  public uploadPsidFile$ = createEffect(() =>
    this._actions$.pipe(
      ofType(ManageSurveyActions.uploadPsidFile),
      switchMap(({ surveyId, file }) =>
        this._psidService.uploadPsidFile(surveyId, file).pipe(
          map((result) => ManageSurveyApiActions.uploadPsidFileSuccess(result)),
          this.psCatchError(
            (err) =>
              of(
                ManageSurveyApiActions.uploadPsidFileFailure({
                  error: err.message ?? 'Failed to upload respondent list',
                }),
              ),
            { notify: true },
          ),
        ),
      ),
    ),
  );

  public downloadRespondentListTemplate$ = createEffect(() =>
    this._actions$.pipe(
      ofType(ManageSurveyActions.downloadRespondentListTemplate),
      switchMap(() =>
        this._surveyService.downloadRespondentListTemplate().pipe(
          map(() =>
            ManageSurveyApiActions.downloadRespondentListTemplateSuccess(),
          ),
          this.psCatchError(
            (err) =>
              of(
                ManageSurveyApiActions.downloadRespondentListTemplateFailure({
                  error: err.message ?? 'Failed to download template',
                }),
              ),
            { notify: true },
          ),
        ),
      ),
    ),
  );

  public saveCrmId$ = createEffect(() =>
    this._actions$.pipe(
      ofType(ManageSurveyActions.saveCrmId),
      mergeMap(({ surveyId, crmId }) =>
        this._surveyService
          .patchSurvey(surveyId, [
            { op: 'replace', path: '/crmId', value: crmId },
          ])
          .pipe(
            mergeMap(() => [
              ManageSurveyApiActions.saveCrmIdSuccess(),
              ManageSurveyActions.updateSurvey({ surveyId, survey: { crmId } }),
              SurveyListActions.updateSurvey({ surveyId, survey: { crmId } }),
            ]),
            this.psCatchError(
              () =>
                of(
                  ManageSurveyApiActions.saveCrmIdFailure({
                    error: 'Error while updating CRM ID',
                  }),
                ),
              { notify: true, customMessage: 'Error while updating CRM ID' },
            ),
          ),
      ),
    ),
  );

  public saveAsanaId$ = createEffect(() =>
    this._actions$.pipe(
      ofType(ManageSurveyActions.saveAsanaId),
      mergeMap(({ surveyId, asanaId }) =>
        this._surveyService
          .patchSurvey(surveyId, [
            { op: 'replace', path: '/asanaId', value: asanaId },
          ])
          .pipe(
            mergeMap(() => [
              ManageSurveyApiActions.saveAsanaIdSuccess(),
              ManageSurveyActions.updateSurvey({
                surveyId,
                survey: { asanaId },
              }),
            ]),
            this.psCatchError(
              () =>
                of(
                  ManageSurveyApiActions.saveAsanaIdFailure({
                    error: 'Error while updating ASANA ID',
                  }),
                ),
              { notify: true, customMessage: 'Error while updating ASANA ID' },
            ),
          ),
      ),
    ),
  );

  public saveBillingNumber$ = createEffect(() =>
    this._actions$.pipe(
      ofType(ManageSurveyActions.saveBillingNumber),
      mergeMap(({ surveyId, billingNumber }) =>
        this._surveyService
          .patchSurvey(surveyId, [
            { op: 'replace', path: '/billing_id', value: billingNumber },
          ])
          .pipe(
            mergeMap(() => [
              ManageSurveyApiActions.saveBillingNumberSuccess(),
              ManageSurveyActions.updateSurvey({
                surveyId,
                survey: { billingId: billingNumber },
              }),
              SurveyListActions.updateSurvey({
                surveyId,
                survey: { billingId: billingNumber },
              }),
            ]),
            this.psCatchError(
              () =>
                of(
                  ManageSurveyApiActions.saveBillingNumberFailure({
                    error: 'Error while updating Billing Number',
                  }),
                ),
              {
                notify: true,
                customMessage: 'Error while updating Billing Number',
              },
            ),
          ),
      ),
    ),
  );

  public saveCustomRedirectField$ = createEffect(() =>
    this._actions$.pipe(
      ofType(ManageSurveyActions.saveCustomRedirectField),
      mergeMap(({ surveyId, value }) =>
        this._surveyService.updateCustomRedirectField(surveyId, value).pipe(
          tap(() =>
            this._toast.success(
              'Success! Custom Redirect Field has been updated.',
              { keepOpen: false },
            ),
          ),
          mergeMap(() => [
            ManageSurveyApiActions.saveCustomRedirectFieldSuccess({ value }),
            ManageSurveyActions.updateSurvey({
              surveyId,
              survey: { supplierCustomRedirectField: value },
            }),
          ]),
          this.psCatchError(
            (err) =>
              of(
                ManageSurveyApiActions.saveCustomRedirectFieldFailure({
                  error:
                    err.message ?? 'Error while updating Custom Redirect Field',
                }),
              ),
            { notify: true },
          ),
        ),
      ),
    ),
  );

  // Each quota effect has its own switchMap, so a new survey's first-page load
  // never cancels an older "Show next" / "Show all" request. Every survey switch
  // dispatches resetSurveyQuotas first, so cancelling there stops a late response
  // for the previous survey from landing in the next one's state.
  private _untilQuotasReset<T>(): MonoTypeOperatorFunction<T> {
    return takeUntil(
      this._actions$.pipe(ofType(ManageSurveyActions.resetSurveyQuotas)),
    );
  }
}
